/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'./api/QueryDefinitionProviderAPI'
], (APIFactory, QueryDefinitionProviderAPI) => {
	class QueryDefinitionProvider {
		static DEFAULT_ROWLIMIT = 3000;
		static SUPPRESSION_NONE = 'none';

		constructor({ content, features }) {
			this.content = content;
			this.features = features;
		}

		initialize() {
			this.features['DataQueryExecution'].registerQueryDefinitionProvider(this.getAPI());
		}

		supports() {
			return true;
		}

		getAPI() {
			if (!this.api) {
				this.api = APIFactory.createAPI(this, [QueryDefinitionProviderAPI]);
			}
			return this.api;
		}

		getType() {
			return 'main';
		}

		getQueryDefinitionList() {
			const definitionList = [];
			const queryService = this.features['Dashboard.QueryService2'];
			const visualization = this.features['Visualization'];
			const summariesEnabled = !this.content.getPropertyValue('hideSummaries');
			const expandEnabled = this.content.getPropertyValue('expandCollapse');
			const suppressionEnabled = this.content.getPropertyValue('suppression') !== QueryDefinitionProvider.SUPPRESSION_NONE;
			const slotsByDataset = this._getSlotsByDataset(visualization);
			const internalVisualization = this.features['Visualization.internal'];
			const filters = [ ...visualization.getLocalFilters().getFilterList(), ...internalVisualization.getSearchFilterList() ];

			for(let datasetId in slotsByDataset) {
				const definition = queryService.createQueryDefinition(datasetId, this.getType());

				definition.setSummariesEnabled(summariesEnabled);
				definition.setExpandEnabled(expandEnabled);
				//There are only two values for suppression: rowsAndColumns and none
				definition.setSuppressionEnabled(suppressionEnabled);
				definition.setDataSource(visualization.getDataSource());

				slotsByDataset[datasetId].forEach(slot => {
					const dataItems = slot.getDataItemList();
					if(slot.getDefinition().isStackItems()) {
						return this._nestDataItems({ definition, dataItems });
					} else {
						return this._composeDataItems({ definition, dataItems });
					}
				});
				if (filters && filters.length > 0) {
					definition.addFilters(filters);
				}
				definitionList.push(definition);
			}
			return Promise.resolve(definitionList);
		}

		/**
		 * Get list of slots for each datasets
		 * @param {VisualizationAPI} visualization
		 * @returns {Object} array of slots mapped by the datasetId as the key
		 */
		_getSlotsByDataset(visualization) {
			const slotMap = {};
			const mappedSlots = visualization.getSlots().getMappedSlotList() || [];
			mappedSlots.forEach((slot) => {
				const slotDefinition = slot.getDefinition();
				const datasetIds = slotDefinition.getDatasetIdList() || [];
				// single slot can be shared among multiple datasets
				datasetIds.forEach((datasetId) => {
					if (!slotMap[datasetId]) {
						slotMap[datasetId] = [slot];
					} else {
						slotMap[datasetId].push(slot);
					}
				});
			});
			return slotMap;
		}

		_composeDataItems({ definition, dataItems }) {
			definition.createDataItemSet(dataItems);
		}

		_nestDataItems({ definition, dataItems }) {
			let parent = definition;
			dataItems.forEach(dataItem => {
				parent = parent.createDataItemSet([dataItem]);
			});
		}
	}
	return QueryDefinitionProvider;
});
